import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';

interface Application {
  id: string;
  email: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  calendly_scheduled_at?: string;
  status: string;
  [key: string]: any;
}

interface InterviewCalendarProps {
  applications: Application[];
  onViewApplication: (id: string) => void;
}

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  interviews: Array<{
    id: string;
    name: string;
    time: string;
    timestamp: number;
  }>;
}

export default function InterviewCalendar({ applications, onViewApplication }: InterviewCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());

  const scheduledApplications = useMemo(() => {
    return applications.filter(app => app.calendly_scheduled_at);
  }, [applications]);

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getCalendarDays = (): CalendarDay[] => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const firstDayOfWeek = firstDayOfMonth.getDay();
    const daysInMonth = lastDayOfMonth.getDate();

    const daysFromPrevMonth = firstDayOfWeek;
    const totalDays = Math.ceil((daysInMonth + daysFromPrevMonth) / 7) * 7;
    const daysFromNextMonth = totalDays - daysInMonth - daysFromPrevMonth;

    const days: CalendarDay[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: date.getTime() === today.getTime(),
        interviews: [],
      });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateString = date.toISOString().split('T')[0];

      const interviewsForDay = scheduledApplications
        .filter(app => {
          if (!app.calendly_scheduled_at) return false;
          const appDate = new Date(app.calendly_scheduled_at);
          const appDateString = appDate.toISOString().split('T')[0];
          return appDateString === dateString;
        })
        .map(app => {
          const appDate = new Date(app.calendly_scheduled_at!);
          return {
            id: app.id,
            name: app.full_name || `${app.first_name || ''} ${app.last_name || ''}`.trim() || app.email,
            time: formatTime(appDate),
            timestamp: appDate.getTime(),
          };
        })
        .sort((a, b) => a.timestamp - b.timestamp);

      days.push({
        date,
        isCurrentMonth: true,
        isToday: date.getTime() === today.getTime(),
        interviews: interviewsForDay,
      });
    }

    for (let day = 1; day <= daysFromNextMonth; day++) {
      const date = new Date(year, month + 1, day);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: date.getTime() === today.getTime(),
        interviews: [],
      });
    }

    return days;
  };

  const calendarDays = getCalendarDays();
  const weeks: CalendarDay[][] = [];
  for (let i = 0; i < calendarDays.length; i += 7) {
    weeks.push(calendarDays.slice(i, i + 7));
  }

  const monthInterviewCount = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    return scheduledApplications.filter(app => {
      if (!app.calendly_scheduled_at) return false;
      const appDate = new Date(app.calendly_scheduled_at);
      return appDate.getFullYear() === year && appDate.getMonth() === month;
    }).length;
  }, [scheduledApplications, currentDate]);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (scheduledApplications.length === 0) {
    return (
      <div className="text-center py-16">
        <CalendarIcon size={64} className="mx-auto mb-4 text-gray-600" />
        <p className="text-gray-400 text-lg mb-2">No scheduled interviews</p>
        <p className="text-gray-500 text-sm">
          Interviews will appear here once they are scheduled via Calendly
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-8">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={goToPreviousMonth}
            className="p-3 hover:bg-gray-700 rounded-lg transition-colors"
            title="Previous month"
          >
            <ChevronLeft size={28} className="text-gray-400" />
          </button>

          <div className="text-center">
            <h2 className="font-horizon text-4xl text-yellow-400 mb-1">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            {monthInterviewCount > 0 && (
              <p className="text-gray-400 text-base">
                {monthInterviewCount} interview{monthInterviewCount !== 1 ? 's' : ''} scheduled
              </p>
            )}
          </div>

          <button
            onClick={goToNextMonth}
            className="p-3 hover:bg-gray-700 rounded-lg transition-colors"
            title="Next month"
          >
            <ChevronRight size={28} className="text-gray-400" />
          </button>
        </div>

        <div className="flex justify-center mb-6">
          <button
            onClick={goToToday}
            className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
          >
            Today
          </button>
        </div>

        <div className="grid grid-cols-7 gap-3">
          {dayNames.map(day => (
            <div
              key={day}
              className="text-center text-yellow-400 font-bold text-base py-3"
            >
              {day}
            </div>
          ))}

          {weeks.map((week, weekIndex) => (
            week.map((day, dayIndex) => (
              <div
                key={`${weekIndex}-${dayIndex}`}
                className={`h-[240px] border rounded-lg p-3 flex flex-col ${
                  day.isCurrentMonth
                    ? 'bg-gray-800/50 border-gray-700'
                    : 'bg-gray-900/30 border-gray-800'
                } ${
                  day.isToday
                    ? 'ring-2 ring-yellow-400'
                    : ''
                } ${
                  day.interviews.length > 0
                    ? 'bg-blue-900/10 border-blue-700/30'
                    : ''
                }`}
              >
                <div
                  className={`text-base font-medium mb-3 flex-shrink-0 ${
                    day.isCurrentMonth ? 'text-white' : 'text-gray-600'
                  } ${
                    day.isToday ? 'text-yellow-400 font-bold' : ''
                  }`}
                >
                  {day.date.getDate()}
                  {day.interviews.length > 5 && (
                    <span className="ml-2 text-xs text-gray-500">
                      ({day.interviews.length})
                    </span>
                  )}
                </div>

                <div className="relative flex-1 overflow-hidden">
                  <div className="space-y-1.5 max-h-full overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-yellow-600/50 scrollbar-track-gray-800/20 hover:scrollbar-thumb-yellow-500/70 scroll-smooth">
                    {day.interviews.map(interview => (
                      <button
                        key={interview.id}
                        onClick={() => onViewApplication(interview.id)}
                        className="w-full text-left px-3 py-2 bg-blue-600/20 hover:bg-blue-600/40 border border-blue-500/30 rounded text-sm transition-colors"
                      >
                        <div className="text-blue-300 font-medium truncate">
                          {interview.name}
                        </div>
                        <div className="text-blue-400/70 text-xs">
                          {interview.time}
                        </div>
                      </button>
                    ))}
                  </div>
                  {day.interviews.length > 5 && (
                    <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-gray-800/90 to-transparent pointer-events-none" />
                  )}
                </div>
              </div>
            ))
          ))}
        </div>
      </div>
    </div>
  );
}
